import React, { forwardRef, useCallback, useEffect, useRef, useState } from 'react';

// Zoom + paneo compartido para comparadores de dos fotos alineadas (misma
// cámara, mismo encuadre). Se transforma UN solo contenedor ("stage") con
// translate+scale, así las dos capas quedan siempre en las mismas coordenadas.
// Todo se aplica de forma imperativa dentro de requestAnimationFrame: mover el
// mouse o pellizcar no re-renderiza React.
//
// Uso: ref={viewportRef} en la caja visible (overflow-hidden), ref={stageRef}
// en un div absolute inset-0 que contiene las imágenes. Los elementos con
// [data-zoom-ignore] (manijas, lente, botones) no disparan paneo/zoom.

const DEFAULT_MAX_SCALE = 6;
const IGNORE = '[data-zoom-ignore]';

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

const formatScale = (s) => `${s.toFixed(1).replace('.', ',')}×`;

export default function useSyncedZoom({ onChange, onShiftWheel, maxScale = DEFAULT_MAX_SCALE } = {}) {
  const maxScaleRef = useRef(maxScale);
  maxScaleRef.current = maxScale;
  const sharpTimer = useRef(0);
  const [node, setNode] = useState(null);
  const viewportNode = useRef(null);
  const stageRef = useRef(null);
  const indicatorRef = useRef(null);
  const state = useRef({ s: 1, x: 0, y: 0 });
  const frame = useRef(0);
  const animateNext = useRef(false);
  const onChangeRef = useRef(onChange);
  const onShiftWheelRef = useRef(onShiftWheel);
  onChangeRef.current = onChange;
  onShiftWheelRef.current = onShiftWheel;

  const viewportRef = useCallback((el) => {
    viewportNode.current = el;
    setNode(el);
  }, []);

  const flush = useCallback(() => {
    frame.current = 0;
    const { s, x, y } = state.current;
    const stage = stageRef.current;
    if (stage) {
      // Nitidez: con will-change el navegador escala un bitmap ya rasterizado
      // (se ve borroso). Lo activamos sólo mientras hay movimiento y al soltar
      // lo sacamos para que vuelva a dibujar la foto 4K al tamaño del zoom.
      stage.style.willChange = 'transform';
      clearTimeout(sharpTimer.current);
      sharpTimer.current = setTimeout(() => { if (stageRef.current) stageRef.current.style.willChange = 'auto'; }, 180);
      stage.style.transition = animateNext.current && !prefersReducedMotion() ? 'transform 200ms ease-out' : 'none';
      stage.style.transform = s === 1 ? '' : `translate(${x}px, ${y}px) scale(${s})`;
    }
    const vp = viewportNode.current;
    if (vp) {
      vp.style.touchAction = s > 1 ? 'none' : 'pan-y';
      vp.style.cursor = s > 1 ? 'grab' : '';
    }
    const ind = indicatorRef.current;
    if (ind) {
      ind.style.display = s > 1.01 ? 'flex' : 'none';
      const label = ind.querySelector('[data-zoom-label]');
      if (label) label.textContent = formatScale(s);
    }
    onChangeRef.current?.(state.current, animateNext.current && !prefersReducedMotion());
    animateNext.current = false;
  }, []);

  const schedule = useCallback((animate = false) => {
    if (animate) animateNext.current = true;
    if (!frame.current) frame.current = requestAnimationFrame(flush);
  }, [flush]);

  const clamp = useCallback(() => {
    const vp = viewportNode.current;
    if (!vp) return;
    const W = vp.clientWidth;
    const H = vp.clientHeight;
    const z = state.current;
    z.s = Math.min(maxScaleRef.current, Math.max(1, z.s));
    z.x = Math.min(0, Math.max(W * (1 - z.s), z.x));
    z.y = Math.min(0, Math.max(H * (1 - z.s), z.y));
  }, []);

  // Zoom anclado al punto (cx, cy) del viewport: ese punto queda quieto.
  const zoomAt = useCallback((factor, cx, cy, animate = false) => {
    const z = state.current;
    const next = Math.min(maxScaleRef.current, Math.max(1, z.s * factor));
    if (next === z.s) return;
    z.x = cx - ((cx - z.x) * next) / z.s;
    z.y = cy - ((cy - z.y) * next) / z.s;
    z.s = next;
    clamp();
    schedule(animate);
  }, [clamp, schedule]);

  const reset = useCallback((animate = true) => {
    state.current = { s: 1, x: 0, y: 0 };
    schedule(animate);
  }, [schedule]);

  // Pantalla (clientX/Y) -> coordenadas del stage sin zoom (px).
  const toStage = useCallback((clientX, clientY) => {
    const rect = viewportNode.current?.getBoundingClientRect();
    const z = state.current;
    if (!rect) return { x: 0, y: 0 };
    return { x: (clientX - rect.left - z.x) / z.s, y: (clientY - rect.top - z.y) / z.s };
  }, []);

  useEffect(() => {
    if (!node) return undefined;
    const pointers = new Map();
    let pan = null;
    let pinch = null;
    let lastTap = 0;
    let downAt = null;

    const local = (clientX, clientY) => {
      const r = node.getBoundingClientRect();
      return [clientX - r.left, clientY - r.top];
    };

    const onWheel = (e) => {
      if (e.target.closest?.(IGNORE) && !e.shiftKey) return;
      if (e.shiftKey && onShiftWheelRef.current) {
        e.preventDefault();
        onShiftWheelRef.current(e);
        return;
      }
      e.preventDefault();
      const [cx, cy] = local(e.clientX, e.clientY);
      const delta = e.deltaMode === 1 ? e.deltaY * 16 : e.deltaY;
      zoomAt(Math.exp(-delta * 0.0015), cx, cy);
    };

    const onPointerDown = (e) => {
      if (e.button > 0 || e.target.closest?.(IGNORE)) return;
      pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      downAt = { x: e.clientX, y: e.clientY, t: Date.now() };
      if (pointers.size === 2) {
        const [a, b] = [...pointers.values()];
        pinch = { d: Math.hypot(a.x - b.x, a.y - b.y) || 1, s: state.current.s };
        pan = null;
      } else if (pointers.size === 1 && state.current.s > 1) {
        pan = { x: e.clientX, y: e.clientY, ox: state.current.x, oy: state.current.y };
        node.style.cursor = 'grabbing';
      }
      if (pinch || pan) node.setPointerCapture?.(e.pointerId);
    };

    const onPointerMove = (e) => {
      if (!pointers.has(e.pointerId)) return;
      pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (pinch && pointers.size >= 2) {
        const [a, b] = [...pointers.values()];
        const d = Math.hypot(a.x - b.x, a.y - b.y) || 1;
        const [cx, cy] = local((a.x + b.x) / 2, (a.y + b.y) / 2);
        zoomAt((pinch.s * d) / pinch.d / state.current.s, cx, cy);
      } else if (pan) {
        state.current.x = pan.ox + (e.clientX - pan.x);
        state.current.y = pan.oy + (e.clientY - pan.y);
        clamp();
        schedule();
      }
    };

    const onPointerUp = (e) => {
      if (!pointers.has(e.pointerId)) return;
      pointers.delete(e.pointerId);
      if (pointers.size < 2) pinch = null;
      if (pointers.size === 0) {
        pan = null;
        if (state.current.s > 1) node.style.cursor = 'grab';
        // Doble toque en pantallas táctiles (dblclick no siempre llega).
        if (e.type === 'pointerup' && e.pointerType === 'touch' && downAt
          && Math.hypot(e.clientX - downAt.x, e.clientY - downAt.y) < 12 && Date.now() - downAt.t < 300) {
          const now = Date.now();
          if (now - lastTap < 320) {
            reset(true);
            lastTap = 0;
          } else {
            lastTap = now;
          }
        }
      }
    };

    const onDblClick = (e) => {
      if (e.target.closest?.(IGNORE)) return;
      reset(true);
    };

    const onKeyDown = (e) => {
      if (e.target !== node) return;
      const W = node.clientWidth;
      const H = node.clientHeight;
      const step = 48;
      const z = state.current;
      let handled = true;
      if (e.key === '+' || e.key === '=') zoomAt(1.25, W / 2, H / 2, true);
      else if (e.key === '-' || e.key === '_') zoomAt(0.8, W / 2, H / 2, true);
      else if (e.key === '0' || e.key === 'Escape') { if (z.s > 1) reset(true); else handled = false; }
      else if (z.s > 1 && e.key.startsWith('Arrow')) {
        if (e.key === 'ArrowLeft') z.x += step;
        if (e.key === 'ArrowRight') z.x -= step;
        if (e.key === 'ArrowUp') z.y += step;
        if (e.key === 'ArrowDown') z.y -= step;
        clamp();
        schedule(true);
      } else handled = false;
      if (handled) e.preventDefault();
    };

    const onResize = () => { clamp(); schedule(); };

    node.style.touchAction = 'pan-y';
    node.addEventListener('wheel', onWheel, { passive: false });
    node.addEventListener('pointerdown', onPointerDown);
    node.addEventListener('pointermove', onPointerMove);
    node.addEventListener('pointerup', onPointerUp);
    node.addEventListener('pointercancel', onPointerUp);
    node.addEventListener('dblclick', onDblClick);
    node.addEventListener('keydown', onKeyDown);
    window.addEventListener('resize', onResize);
    return () => {
      node.removeEventListener('wheel', onWheel);
      node.removeEventListener('pointerdown', onPointerDown);
      node.removeEventListener('pointermove', onPointerMove);
      node.removeEventListener('pointerup', onPointerUp);
      node.removeEventListener('pointercancel', onPointerUp);
      node.removeEventListener('dblclick', onDblClick);
      node.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('resize', onResize);
    };
  }, [node, zoomAt, reset, clamp, schedule]);

  useEffect(() => () => {
    if (frame.current) cancelAnimationFrame(frame.current);
    clearTimeout(sharpTimer.current);
  }, []);

  return { viewportRef, viewportNode, stageRef, indicatorRef, state, reset, toStage, schedule };
}

const isCoarse = () => typeof window !== 'undefined' && window.matchMedia?.('(pointer: coarse)').matches;

// Pastilla "2,3× · doble clic para volver". La muestra/oculta el hook.
export const ZoomIndicator = forwardRef(({ onReset }, ref) => (
  <button
    ref={ref}
    type="button"
    data-zoom-ignore
    onClick={onReset}
    style={{ display: 'none' }}
    className="absolute bottom-2 left-2 z-20 items-center gap-1.5 bg-white/95 text-gray-700 text-xs px-2.5 py-1 rounded-full shadow-sm border border-gray-200 hover:bg-white"
    title="Volver al tamaño original"
  >
    <span data-zoom-label className="font-semibold text-blue-700">1,0×</span>
    <span className="text-gray-500">· {isCoarse() ? 'doble toque' : 'doble clic'} para volver</span>
  </button>
));
ZoomIndicator.displayName = 'ZoomIndicator';
